'use client';

import React from 'react';
import { JsonLdScript } from './JsonLdScript';
import { stripCitations } from '@/lib/strip-citations';

/**
 * JSON-LD FAQPage schema for pages with FAQ content.
 * Enables rich FAQ results in Google search.
 *
 * Citation markers (`[source-id]`) are stripped only when they resolve against
 * the same source map used by the visible FAQ. Other bracketed prose remains
 * literal, matching CitedText.
 *
 * @see https://schema.org/FAQPage
 * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */

interface FAQ {
  question: string;
  answer: string;
}

interface FAQPageSchemaProps {
  faqs: FAQ[];
  /** Source IDs available to the visible FAQ's CitationProvider. */
  sourceIds?: readonly string[];
}

export function FAQPageSchema({ faqs, sourceIds = [] }: FAQPageSchemaProps): React.ReactElement | null {
  if (!faqs || faqs.length === 0) {
    return null;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: stripCitations(faq.question, sourceIds),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripCitations(faq.answer, sourceIds),
      },
    })),
  };

  return <JsonLdScript schema={schema} />;
}
